/* eslint-disable import/no-cycle */
import { adminApi } from '@/includes/contentManagementApi';

export default {
  /**
   * Create a new chart
   * @param {Object} chartData - The chart data to create
   * @returns {Promise<Object>} The created chart
   */
  createChart(chartData) {
    return adminApi.post('/charts', chartData);
  },

  /**
   * Get all charts
   * @returns {Promise<Array>} Array of charts
   */
  getAllCharts() {
    return adminApi.get('/charts');
  },

  /**
   * Get a chart by ID
   * @param {string} id - The chart ID
   * @returns {Promise<Object>} The chart data
   */
  getChartById(id) {
    return adminApi.get(`/charts/${id}`);
  },

  /**
   * Get a chart by assessment ID
   * @param {string} assessmentId - The assessment ID
   * @returns {Promise<Object>} The chart data
   */
  getChartByAssessmentId(assessmentId) {
    return adminApi.get(`/charts/assessment/${assessmentId}`);
  },

  /**
   * Update a chart
   * @param {string} id - The chart ID
   * @param {Object} chartData - The updated chart data
   * @returns {Promise<Object>} The updated chart
   */
  updateChart(id, chartData) {
    return adminApi.patch(`/charts/${id}`, chartData);
  },

  /**
   * Delete a chart
   * @param {string} id - The chart ID
   * @returns {Promise<void>}
   */
  deleteChart(id) {
    return adminApi.delete(`/charts/${id}`);
  },

  /**
   * Get linkable assessments for a chart
   * @param {string} assessmentId - The current assessment ID
   * @param {Array<string>} dimensions - Array of dimension IDs
   * @returns {Promise<Array>} Array of linkable assessments
   */
  getLinkableAssessments(assessmentId, dimensions) {
    return adminApi.get('/assessments/linkable', {
      params: {
        assessmentId,
        dimensions: dimensions.join(','),
      },
    });
  },

  /**
   * Get chart data structure for creating/updating charts
   * @returns {Object} Default chart data structure matching the backend DTO
   */
  getDefaultChartData() {
    return {
      type: 'bar', // Must be one of: 'bar', 'line', 'pie', 'doughnut'
      title: {
        en: '', // Required string
        de: '', // Required string
      },
      description: {
        en: '', // Required string
        de: '', // Required string
      },
      data: {
        labels: [], // Array of strings
        datasets: [
          {
            label: '', // Required string
            data: [], // Array of numbers
            backgroundColor: 'rgba(255, 99, 132, 0.5)', // Optional string
            borderColor: 'rgb(255, 99, 132)', // Optional string
          },
        ],
      },
      dimensions: [], // Array of strings
      options: {}, // Optional object
      visibility: 'both', // 'own' | 'team' | 'both'
    };
  },

  /**
   * Validate chart data before sending to the server
   * @param {Object} chartData - The chart data to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateChartData(chartData) {
    const errors = [];

    // Validate type
    if (!['bar', 'line', 'pie', 'doughnut'].includes(chartData.type)) {
      errors.push('type must be one of: bar, line, pie, doughnut');
    }

    // Validate title
    if (!chartData.title || typeof chartData.title !== 'object') {
      errors.push('title must be an object');
    } else {
      if (typeof chartData.title.en !== 'string') errors.push('title.en must be a string');
      if (typeof chartData.title.de !== 'string') errors.push('title.de must be a string');
    }

    // Validate description
    if (!chartData.description || typeof chartData.description !== 'object') {
      errors.push('description must be an object');
    } else {
      if (typeof chartData.description.en !== 'string') {
        errors.push('description.en must be a string');
      }
      if (typeof chartData.description.de !== 'string') {
        errors.push('description.de must be a string');
      }
    }

    // Validate data
    if (!chartData.data || typeof chartData.data !== 'object') {
      errors.push('data must be an object');
    } else {
      if (!Array.isArray(chartData.data.labels)) errors.push('data.labels must be an array');
      if (!Array.isArray(chartData.data.datasets)) errors.push('data.datasets must be an array');

      chartData.data.datasets.forEach((dataset, index) => {
        if (typeof dataset.label !== 'string') {
          errors.push(`dataset[${index}].label must be a string`);
        }
        if (!Array.isArray(dataset.data)) errors.push(`dataset[${index}].data must be an array`);
        if (dataset.backgroundColor && typeof dataset.backgroundColor !== 'string') {
          errors.push(`dataset[${index}].backgroundColor must be a string`);
        }
        if (dataset.borderColor && typeof dataset.borderColor !== 'string') {
          errors.push(`dataset[${index}].borderColor must be a string`);
        }
      });
    }

    // Validate dimensions
    if (!Array.isArray(chartData.dimensions)) {
      errors.push('dimensions must be an array');
    } else {
      chartData.dimensions.forEach((dim, index) => {
        if (typeof dim !== 'string') {
          errors.push(`dimensions[${index}] must be a string`);
        }
      });
    }

    if (chartData.visibility != null && !['own', 'team', 'both'].includes(chartData.visibility)) {
      errors.push('visibility must be one of: own, team, both');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};
